import {Animation} from '@/components/ui/animation/Animation'
import {CashbackBanner} from '@/components/ui/cashback-banner/CashbackBanner'
import {Modal} from '@/components/ui/modal/Modal'
import {Spinner} from '@/components/ui/spinner/Spinner'
import {useMe} from '@/features/authentication/queries/useMe'
import {GuestTypeV3} from '@/features/authentication/types'
import {BillingInfoModal} from '@/features/billing/components/billing-info-modal/BillingInfoModal'
import {PlansList} from '@/features/configure-membership-plans/configure-membership-plans-modal/components/plans-list/PlansList'
import {GenderKeyNameE} from '@/features/constants/constants'
import {ShareProfileModal} from '@/features/host/components/share-profile-modal/ShareProfileModal'
import {remapCurrency} from '@/features/profile/constants'
import useRedirectToChatChannel from '@/hooks/useRedirectToChatChannel'
import useMixPanel from '@/integrations/mixpanel/hooks/useMixpanel'
import {MixpanelEventNameE} from '@/integrations/mixpanel/types'
import {TagManager} from '@/integrations/tag-manager/tagManager'
import membership from '@assets/images/new-icons/membership.png'
import {useRootStore} from '@/store'
import {
    selectChoosePaymentsMethodModal,
    selectErrorModal,
    selectRecurringGiftModal,
    selectSendMegaGiftModal
} from '@/store/selectors'
import {UserRoleE} from '@/types'
import {ROUTE_UPDATE_CREDIT_CARD} from '@/utilities/constants/routeNames'
import {USER_ROLE_GUEST} from '@/utilities/constants/user'
import {LocalStorageManager} from '@/utilities/localStorage'
import successGiftedAnimation from '@assets/animations/Success-Gifted.json'
import {FC, useEffect, useMemo, useState} from 'react'
import {Trans, useTranslation} from 'react-i18next'
import {useLocation, useNavigate} from 'react-router-dom'
import {DonationType, DonationV3} from '../types'
import {
    StyledAnimation,
    StyledBannerWrapper,
    StyledButtonsContainer,
    StyledContentTitle,
    StyledDeclaimer,
    StyledFooter,
    StyledGiftModalStepContainer,
    StyledModalTitle,
    StyledNextPageButton,
    StyledRedirectToChatButton,
    StyledShareButton,
    StyledSuccessBox,
    StyledSuccessButtonBox,
    StyledSuccessContainer,
    StyledSuccessSubTitle,
    StyledSuccessTitle,
    StyledSuccessTitleBox,
    modalStyles
} from './style'
import {InfoIcon} from '@/assets/icons/icons'
import {ToolTipActionsModal} from '@/features/profile/components/host-dashboard/actions-section/components/toolTipActionsModal/ToolTipActionsModal'
import DowngradePlanModal from './downgrade-plan-modal/DowngradePlanModal'
import {SubscriptionStatusesE} from '@/features/chat/chat.model'
import {useGetCreatorProfile} from '@/features/guest/queries/useGetCreatorProfile'
import {useSubscribeToHostV3} from '../queries/useSubscribeToHostV3'
import {useGetSubscribedHostsV3} from '../queries/useGetSubscribedHostsV3'

type GiftModalStepProps = {
    changeStep: (step: 1 | 2 | 3) => void
}

const GIFT_MODAL_STEPS_MAP = {
    1: (props: GiftModalStepProps) => <GiftModalChangeAmount {...props} />,
    2: (props: GiftModalStepProps) => <GiftModalAddCard {...props} />,
    3: (props: GiftModalStepProps) => <GiftModalSuccess {...props} />
}
export const MEGA_GIFT_ID = 'mega-gift'

export const GiftModal: FC = () => {
    const {t} = useTranslation()
    const {closeModal: closeGiftModal, hostID} = useRootStore(selectRecurringGiftModal)
    const {data: host} = useGetCreatorProfile({creatorIdentifier: hostID})

    const [step, setPage] = useState<1 | 2 | 3>(1)

    return (
        <Modal
            showConfettiAnimation
            onClose={() => {
                closeGiftModal()
            }}
            fullScreen
            height={'550px'}
            maxHeight={'600px'}
            width={'400px'}
            title={
                <StyledModalTitle>
                    {t('gift_rookie:title_send_gift', {
                        USER: host?.fullName
                    })}
                </StyledModalTitle>
            }
            modalStyles={modalStyles}
        >
            {GIFT_MODAL_STEPS_MAP[step]?.({
                changeStep: setPage
            })}
        </Modal>
    )
}

const GiftModalChangeAmount: FC<GiftModalStepProps> = ({changeStep}) => {
    const {t} = useTranslation()
    const {data} = useMe()
    const [showInfoModal, setShowInfoModal] = useState(false)
    const [showDowngradeModal, setShowDowngradeModal] = useState(false)
    const user = data?.type === USER_ROLE_GUEST ? (data as GuestTypeV3) : null
    const currencySymbol = remapCurrency[user?.currency || 'USD']
    const ultimateOffer = user?.typeAttributes?.ultimateOffer.isUltimateStatusOfferAvailable
    const cashBackOffer = user?.typeAttributes?.cashbackOffer?.isAvailableCashbackOffer
    const navigate = useNavigate()
    const location = useLocation()
    const redirectUrl = location.pathname

    const {
        hostID,
        closeModal: closeGiftModal,
        updateModalDate,
        minAmountToOpenConnection,
        selectedAmount
    } = useRootStore(selectRecurringGiftModal)
    const {data: host} = useGetCreatorProfile({creatorIdentifier: hostID})
    const {openModal: openChoosePaymentsMethodModal} = useRootStore(selectChoosePaymentsMethodModal)
    const {openModal: openSendMegaGiftModal} = useRootStore(selectSendMegaGiftModal)
    const {data: subscriptionHosts} = useGetSubscribedHostsV3({
        enabled: true,
        status: SubscriptionStatusesE.ACTIVE
    })

    const activeSubscription = subscriptionHosts?.find(
        (subsriptionsHost: DonationV3) => subsriptionsHost.rookieId.toString() === hostID
    )
    const {
        mutate: subscribeToHost,
        data: subscribeToHostResponse,
        error: subscribeToHostError,
        isPending: isPendingSubscribeToHost
    } = useSubscribeToHostV3()
    const {openErrorModal} = useRootStore(selectErrorModal)
    const hasBillingData = !!user?.typeAttributes?.hasInvoicingProfile
    const [showBillingInfoModal, setShowBillingInfoModal] = useState(false)

    const onSuccessHandler = () => {
        if (user && hostID && selectedAmount) {
            TagManager.dataLayer({
                event: 'giftmenu_pop_click_redirect'
            })
            subscribeToHost({
                hostID: +hostID,
                subscriptionId: +selectedAmount?.id,
                userDiscountId: selectedAmount?.discount?.id
            })
        }
    }
    const onSuccess = () => {
        if (!hasBillingData) {
            setShowBillingInfoModal(true)
        } else {
            if (activeSubscription && selectedAmount) {
                if (activeSubscription?.amount > selectedAmount?.amount) setShowDowngradeModal(true)
                else onSuccessHandler()
            } else onSuccessHandler()
        }
    }
    const mixpanel = useMixPanel()
    const eventData = {
        eventType: MixpanelEventNameE.GiftMorgiClick,
        userRole: UserRoleE.Guest,
        userId: `${hostID}`
    }
    useEffect(() => {
        LocalStorageManager.openGiftModal.remove()
        mixpanel?.triggerBackendEvent({...eventData})
        mixpanel?.startTimeEvent(MixpanelEventNameE.CheckoutContinue)

        TagManager.dataLayer({
            event: 'giftmenu_pop_view',
            email: user?.email,
            fullName: user?.fullName,
            userName: user?.username,
            userId: user?.id,
            country: user?.signupCountry?.alpha2Code ?? user?.signupCountry?.id,
            currency: user?.currency,
            micromorgiBalance: user?.typeAttributes.microMorgiBalance,
            userType: user?.type
        })
    }, [])
    useEffect(() => {
        if (subscribeToHostError) {
            if (subscribeToHostError.response?.status === 303) {
                openChoosePaymentsMethodModal({
                    apiCallback: onSuccess,
                    paymentMethodUrls: subscribeToHostError.response?.data?.url ?? null,
                    description: 'choose_payment_method:description_gift',
                    currencyAmount: selectedAmount?.dollar_amount,
                    rookieName: host?.typeAttributes.firstName,
                    isMonthlySupport: true
                })
                mixpanel?.trackEvent(MixpanelEventNameE.CheckoutContinue)
            } else {
                closeGiftModal()
                openErrorModal({message: subscribeToHostError.message || null})
            }
        }
        if (subscribeToHostResponse) {
            changeStep(3)
        }
    }, [subscribeToHostResponse, subscribeToHostError])

    const onSelectGift = (selectedAmount: DonationType) => {
        if (selectedAmount.id === MEGA_GIFT_ID) {
            openSendMegaGiftModal({hostID})
            closeGiftModal()
            return
        }

        updateModalDate({selectedAmount})
    }

    if (isPendingSubscribeToHost) return <Spinner />

    return (
        <StyledGiftModalStepContainer>
            <div>
                <StyledContentTitle>
                    {t('gift_rookie:subtitle_send_gift')}
                    <div style={{display: 'flex'}} onClick={() => setShowInfoModal(true)}>
                        <InfoIcon />
                    </div>
                </StyledContentTitle>
            </div>

            {(ultimateOffer || cashBackOffer) && (
                <StyledBannerWrapper>
                    <CashbackBanner />
                </StyledBannerWrapper>
            )}
            <PlansList
                selectedPlan={selectedAmount || null}
                onSelectPlanHandler={onSelectGift}
                columnNames={['gift_rookie:price', 'gift_rookie:what_you_get']}
                disabledByMinAmount={minAmountToOpenConnection}
                hostID={hostID as string}
                renderMegaphoneText={(plan, numberOfOthersGroups, t) => (
                    <>
                        {plan.group?.name}
                        {numberOfOthersGroups ? ` & ${numberOfOthersGroups} ${t?.('common:others')}` : ''}
                    </>
                )}
            />

            <StyledFooter>
                <StyledButtonsContainer>
                    <StyledNextPageButton
                        className="prev"
                        variant="outlined"
                        fullWidth={false}
                        onClick={closeGiftModal}
                    >
                        {t('common:back')}
                    </StyledNextPageButton>
                    <StyledNextPageButton
                        className="next"
                        variant="primary"
                        fullWidth={false}
                        onClick={onSuccess}
                        disabled={
                            isPendingSubscribeToHost ||
                            !selectedAmount ||
                            selectedAmount.amount === activeSubscription?.amount
                        }
                    >
                        {t('common:continue')}
                    </StyledNextPageButton>
                </StyledButtonsContainer>

                {selectedAmount && (
                    <StyledDeclaimer>
                        {!user?.typeAttributes?.preferredPaymentMethod?.last4 ? (
                            t('gift_rookie:disclaimer:new_transaction', {
                                CURRENCY: currencySymbol,
                                AMOUNT: selectedAmount.dollar_amount
                            })
                        ) : (
                            <Trans
                                i18nKey={'gift_rookie:disclaimer:change_payment_method'}
                                values={{
                                    LAST_4: user.typeAttributes.preferredPaymentMethod.last4
                                }}
                                components={{
                                    span: (
                                        <span
                                            onClick={() => {
                                                navigate(ROUTE_UPDATE_CREDIT_CARD)
                                                !!hostID &&
                                                    LocalStorageManager.openGiftModal.set({redirectUrl, hostId: hostID})
                                                closeGiftModal()
                                            }}
                                        ></span>
                                    )
                                }}
                            />
                        )}
                    </StyledDeclaimer>
                )}
            </StyledFooter>
            {showBillingInfoModal && (
                <BillingInfoModal
                    onClose={() => setShowBillingInfoModal(false)}
                    onSendBillingInfoSuccess={() =>
                        user &&
                        hostID &&
                        selectedAmount &&
                        subscribeToHost({
                            hostID: +hostID,
                            subscriptionId: +selectedAmount.id,
                            userDiscountId: selectedAmount?.discount?.id
                        })
                    }
                />
            )}
            {showInfoModal && (
                <ToolTipActionsModal
                    content={{
                        title: t('configure_membership_plans_modal:info_modal:title'),
                        icon: () => <img src={membership} />,
                        content: 'gift_rookie:info_tooltip'
                    }}
                    onClose={() => setShowInfoModal(false)}
                />
            )}
            {showDowngradeModal && (
                <DowngradePlanModal onClose={() => setShowDowngradeModal(false)} action={onSuccessHandler} />
            )}
        </StyledGiftModalStepContainer>
    )
}

const GiftModalAddCard: FC<GiftModalStepProps> = () => {
    return <StyledGiftModalStepContainer></StyledGiftModalStepContainer>
}

const GiftModalSuccess: FC<GiftModalStepProps> = () => {
    const {t} = useTranslation()
    const location = useLocation()
    const {selectedAmount, hostID, closeModal: closeRecurringGiftModal} = useRootStore(selectRecurringGiftModal)
    const {data: user} = useMe()
    const {data: host} = useGetCreatorProfile({creatorIdentifier: hostID})
    const redirectToChatChannel = useRedirectToChatChannel({receiverId: parseInt(`${hostID}`)})
    const [isOpenShareProfileModal, setIsOpenShareProfileModal] = useState(false)

    const hostGender = useMemo(() => {
        if (host) {
            switch (host?.genderId) {
                case 1:
                    return GenderKeyNameE.MALE
                case 2:
                    return GenderKeyNameE.FEMALE
                default:
                    return GenderKeyNameE.OTHER
            }
        }
    }, [host])

    useEffect(() => {
        TagManager.dataLayer({
            event: 'giftThankYouPage',
            currentUrl: location?.pathname
        })
    }, [])

    return (
        <StyledGiftModalStepContainer>
            <StyledBannerWrapper>
                <CashbackBanner />
            </StyledBannerWrapper>
            <StyledSuccessContainer>
                <StyledSuccessBox>
                    <StyledAnimation>
                        <Animation loop autoplay={true} animationData={successGiftedAnimation} />
                    </StyledAnimation>
                    <div>
                        <StyledSuccessTitleBox>
                            <StyledSuccessTitle>
                                {t('gift_rookie:success:title', {
                                    Morgis: selectedAmount?.amount,
                                    USER: host?.fullName
                                })}
                            </StyledSuccessTitle>
                        </StyledSuccessTitleBox>
                        <StyledSuccessTitleBox>
                            <StyledSuccessSubTitle>
                                {t('gift_rookie:success:description', {
                                    USER: host?.fullName
                                })}
                            </StyledSuccessSubTitle>
                        </StyledSuccessTitleBox>
                    </div>
                </StyledSuccessBox>
                <StyledSuccessButtonBox>
                    <StyledRedirectToChatButton
                        onClick={() => {
                            if (user && hostID) {
                                redirectToChatChannel?.()
                                closeRecurringGiftModal()
                            }
                        }}
                    >
                        {t('gift_rookie:go_to_chat')}
                    </StyledRedirectToChatButton>
                    <StyledShareButton onClick={() => setIsOpenShareProfileModal(true)} variant="outlined">
                        {t('gift_rookie:share_with_friends')}
                    </StyledShareButton>
                </StyledSuccessButtonBox>
            </StyledSuccessContainer>

            {isOpenShareProfileModal && (
                <ShareProfileModal
                    hostGender={hostGender}
                    username={`${host?.username}`}
                    onClose={() => setIsOpenShareProfileModal(false)}
                    type="gift"
                />
            )}
        </StyledGiftModalStepContainer>
    )
}
